import React from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, Compass, Sparkles } from "lucide-react";

/**
 * Large, hand-drawn-style curved arrow displayed in the top-right of the
 * viewport. It visually points to the real "..." button of the social app's
 * webview navigation bar so the user immediately knows where to tap.
 *
 * Pure SVG + CSS animation, no external deps.
 */
function TopRightArrow({ label }) {
  return (
    <div
      className="pointer-events-none absolute top-2 right-2 sm:top-3 sm:right-3 z-10 select-none"
      data-testid="inapp-top-right-arrow"
      style={{ width: 128, height: 148 }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes qriseArrowBob {
          0%, 100% { transform: translate(0px, 0px); }
          50%      { transform: translate(4px, -6px); }
        }
        @keyframes qriseArrowDraw {
          from { stroke-dashoffset: 260; opacity: 0; }
          to   { stroke-dashoffset: 0;   opacity: 1; }
        }
        .qrise-arrow-anim   { animation: qriseArrowBob 1.6s ease-in-out infinite; transform-origin: 100% 0%; }
        .qrise-arrow-path   { stroke-dasharray: 260; animation: qriseArrowDraw 900ms ease-out 100ms both; }
        .qrise-arrow-head   { animation: qriseArrowDraw 500ms ease-out 900ms both; stroke-dasharray: 40; }
        .qrise-arrow-label  {
          animation: qriseArrowDraw 400ms ease-out 1300ms both;
          stroke-dasharray: 200;
        }
      `}</style>
      <svg
        viewBox="0 0 128 148"
        width="128"
        height="148"
        className="qrise-arrow-anim overflow-visible"
        fill="none"
      >
        <defs>
          <linearGradient id="qriseArrowGrad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
        </defs>
        {/* Curved shaft — starts mid-left (near the inline "..." step),
            curves up and to the right, ending near top-right corner. */}
        <path
          className="qrise-arrow-path"
          d="M12 138 C 40 120, 70 90, 92 40"
          stroke="url(#qriseArrowGrad)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Arrow head pointing to top-right */}
        <path
          className="qrise-arrow-head"
          d="M92 40 L 80 50 M92 40 L 86 26"
          stroke="url(#qriseArrowGrad)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Small handwritten-style label */}
        <text
          x="26"
          y="82"
          fill="#7c3aed"
          style={{
            fontFamily:
              "'Caveat','Kalam','Shadows Into Light',cursive,sans-serif",
            fontSize: 22,
            fontWeight: 700,
            transform: "rotate(-10deg)",
            transformOrigin: "26px 82px",
          }}
          className="qrise-arrow-label"
        >
          {label}
        </text>
      </svg>
    </div>
  );
}

/**
 * Regex covering common in-app browsers that cause broken redirections
 * and OAuth issues. Matched case-insensitively against navigator.userAgent.
 *
 * Covers: Facebook (FBAN/FBAV), Facebook Messenger (FBAV; FB_IAB),
 * Instagram, TikTok, Snapchat, LINE, WeChat (MicroMessenger), Twitter/X
 * webviews, LinkedIn, Pinterest.
 */
const IN_APP_BROWSER_RE =
  /(FBAN|FBAV|FB_IAB|FBIOS|Instagram|TikTok|Snapchat|Line\/|MicroMessenger|WeChat|Twitter|LinkedInApp|Pinterest)/i;

export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return IN_APP_BROWSER_RE.test(ua);
}

/**
 * Premium lockscreen shown when the public game page is loaded inside a
 * social-app webview. Matches the QRISE design language: light glassmorphism
 * aqua/lavender background, violet gradient accents, elegant typography.
 *
 * Props:
 *   merchantName:   optional — displayed under the logo if present
 *   merchantLogo:   optional — rendered as a circular badge at the top
 */
export default function InAppBrowserBlocker({
  merchantName = null,
  merchantLogo = null,
}) {
  const { t } = useTranslation();

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-5 py-10 overflow-hidden"
      data-testid="in-app-browser-blocker"
    >
      {/* Animated arrow pointing to the real "..." button of the webview */}
      <TopRightArrow label={t("inapp.arrow_label")} />
      {/* Background — aqua/lavender glassmorphism, consistent with public pages */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(135deg, #eef7ff 0%, #f4efff 55%, #fff6fb 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -z-10 top-[-10%] right-[-10%] h-96 w-96 rounded-full blur-3xl opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.25) 0%, rgba(139,92,246,0) 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -z-10 bottom-[-10%] left-[-10%] h-96 w-96 rounded-full blur-3xl opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(14,165,233,0.25) 0%, rgba(14,165,233,0) 70%)",
        }}
      />

      <div className="relative max-w-md w-full">
        {/* QRISE or merchant logo */}
        <div className="flex justify-center mb-8">
          {merchantLogo ? (
            <div
              className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-white overflow-hidden"
              style={{
                boxShadow:
                  "0 18px 40px -12px rgba(10,12,24,0.3), 0 0 0 4px rgba(255,255,255,0.6)",
              }}
              title={merchantName || ""}
            >
              <img
                src={merchantLogo}
                alt={merchantName || "logo"}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                boxShadow: "0 14px 30px -10px rgba(139,92,246,0.55)",
              }}
            >
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-bold tracking-[0.18em] text-white">
                QRISE
              </span>
            </div>
          )}
        </div>

        <div className="qrise-card p-8 sm:p-10 text-center">
          <div
            className="inline-flex items-center justify-center h-14 w-14 rounded-full mb-5"
            style={{
              background:
                "linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(236,72,153,0.12) 100%)",
            }}
          >
            <Compass className="h-6 w-6 text-violet-600" />
          </div>
          <h1 className="text-2xl sm:text-[1.75rem] font-semibold tracking-tight leading-tight">
            {t("inapp.title")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            {t("inapp.subtitle")}
          </p>

          <div className="mt-8 space-y-4 text-left">
            <Step
              number={1}
              body={
                <span>
                  {t("inapp.step1_a")}{" "}
                  <span
                    className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-muted text-foreground mx-0.5 align-middle"
                    aria-hidden="true"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </span>{" "}
                  {t("inapp.step1_b")}
                </span>
              }
            />
            <Step
              number={2}
              body={
                <span>
                  {t("inapp.step2_a")}{" "}
                  <strong
                    className="font-semibold"
                    style={{ color: "#16a34a" }}
                  >
                    &ldquo;{t("inapp.step2_action")}&rdquo;
                  </strong>
                </span>
              }
            />
          </div>

          <div className="mt-8 pt-6 border-t border-border/60">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              {t("inapp.why_label")}
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
              {t("inapp.why_body")}
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-muted-foreground">
          {t("inapp.powered_by")}
        </p>
      </div>
    </div>
  );
}

function Step({ number, body }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full font-bold text-sm"
        style={{
          background:
            "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
          color: "white",
          boxShadow: "0 6px 16px -6px rgba(139,92,246,0.5)",
        }}
      >
        {number}
      </div>
      <div className="flex-1 pt-1 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
